// Copy for /services/designer-toy-production/ (code page since 2026-11-04).
// Production runs first: molded vinyl from 300 pieces and resin runs. Every
// number is a published figure term from .seo/truth.md (2026-09-29 offer and
// the 2026-11-03 agency brief): about $450 for a simple 6-inch resin
// prototype (3–4 weeks, 1–2 revisions), resin runs of 1 to 100 with a $450
// minimum budget, larger hand-painted resin runs quoted (500 figures for an
// ad agency's client gift), vinyl from 300 units with a mold fee of about
// $1,500 per character design plus production. No articulated action
// figures. Where the vinyl run is made, its schedule and toy-safety testing
// are not published, so the page says the quote confirms them.
const { figurePricing } = require('./custom-figurines')

const page = {
  path: '/services/designer-toy-production/',
  reviewedDate: '2026-11-04',
  reviewedDateLabel: 'November 4, 2026',
  title: 'Designer Toy Production: Vinyl & Resin Toy Manufacturer',
  description: `Designer toy production for artists and brands: vinyl runs from ${figurePricing.vinylMinimum} pieces (mold about $${figurePricing.vinylMold} per design) and hand-painted resin runs, from a $${figurePricing.prototype} prototype.`,
  answer: `PrintAWorld is a New York designer toy production studio for artists, brands and agencies. We sculpt your character from a sketch, AI image or 3D file, make a painted resin prototype for you to approve (about $${figurePricing.prototype} for a simple ${figurePricing.prototypeHeight} figure, typically ${figurePricing.prototypeWeeks}), then produce the run: molded vinyl from ${figurePricing.vinylMinimum} pieces, with a mold fee of about $${figurePricing.vinylMold} per character design, or hand-painted resin from 1 to 100 and larger runs quoted. We ship to all 50 states.`,
}

const assetRoot = 'https://images.ctfassets.net/dovsnf6qrcem/'
const photoUrl = (photo, width) =>
  `${assetRoot}${photo.file}?w=${width}&q=80&fm=webp`

// Studio photos already published (and checked for licensed characters) on
// /custom-vinyl-figures-prototyping/. Captions say what the piece is.
const photos = {
  runLineup: {
    file: '533MFmSvJoxpgXMtau14cx/a4b11a79881bd96fc13134bb4d4ca0db/_DSC0054.jpg',
    alt: 'A row of matching unpainted resin astronaut figures from one production run',
    ratio: 1.5,
  },
  colorways: {
    file: '7GyRfBvNotzUHknZi6veOj/c3513166f6a9509cdd491221cd8fee76/_DSC8265.jpg',
    alt: 'Six versions of a horned character in black, white, clear, gold, rainbow and pink finishes',
    ratio: 1.5,
  },
  bananaTrio: {
    file: '3LEM2rVusLCf5zfBqIaTnM/b7b7899141f2954972d7e6a769e7c7db/IMG_2464.jpg',
    alt: 'Three banana-shaped character figures with long hair, painted in purple, red and teal colorways on round bases',
    ratio: 1.5,
  },
  armoredSet: {
    file: '6dniUJjX0HfqDR6jeJutlQ/1c7eda4202c707cd1fa58b6182972083/LAL_6011.jpg',
    alt: 'Three matching gray armored character figures lined up in the studio',
    ratio: 1.5,
  },
  iceFishing: {
    file: '60InyWto98GyPLmFb5YTFF/f1bb2f7f042a830cb36f9a34455efa56/LAL_0497-4.jpg',
    alt: 'Four green characters ice fishing on sculpted ice bases, each with a rod, a bucket and a fishing hole',
    ratio: 1.5,
  },
  mascotAward: {
    file: '1pKmfmcrvaMhC7m7ga1BBt/72c7e7c46fc3890ee48d9ac3587aaf7d/LAL_8963.jpg',
    alt: 'Green shopping-bag mascot figure with arrows on its head standing on a round white award base',
    ratio: 1.5,
  },
  dessertSet: {
    file: '4iZxZFC8sTOTMstTm1GG7m/9c5f1e88eb7c75d11c85fdf2ccf318db/LAL_6779.jpg',
    alt: 'A group of white dessert-shaped art figures with red accents and different surface details',
    ratio: 1.5,
  },
}

const hero = 'colorways'

const gallery = [
  {
    name: 'runLineup',
    layout: 'feature',
    caption: 'One sculpt, printed as a matching run',
    sizes: '(max-width: 760px) 100vw, 50vw',
  },
  { name: 'bananaTrio', caption: 'One character in three colorways' },
  { name: 'armoredSet', caption: 'A matched set, ready for paint' },
  {
    name: 'iceFishing',
    layout: 'wide',
    caption: 'A four-figure series on sculpted bases',
    sizes: '(max-width: 760px) 100vw, 50vw',
  },
  { name: 'mascotAward', caption: 'A brand mascot made physical' },
  { name: 'dessertSet', caption: 'An art-toy series with surface variations' },
]

// The three cards under the hero (no stats row): pick a quantity, see its
// price and timing, jump to its section.
const routes = [
  {
    href: '#prototype',
    icon: 'one',
    need: 'Before the run',
    name: 'Resin prototype',
    price: `About $${figurePricing.prototype}`,
    detail: `One ${figurePricing.prototypeHeight} figure, modeled and painted. Typically ${figurePricing.prototypeWeeks}.`,
    more: 'The prototype',
  },
  {
    href: '#resin-runs',
    icon: 'run',
    need: '1 to 100 and up',
    name: 'Hand-painted resin run',
    price: 'Quoted per project',
    detail: `$${figurePricing.resinMinimumBudget} minimum budget. Larger runs quoted case by case.`,
    more: 'Resin runs',
  },
  {
    href: '#vinyl-runs',
    icon: 'mold',
    need: `${figurePricing.vinylMinimum} or more`,
    name: 'Molded vinyl run',
    price: `About $${figurePricing.vinylMold} mold`,
    detail: 'Per character design, plus the cost of producing the figures.',
    more: 'Vinyl production',
  },
]

// "At a glance": the facts AI answers already quote about this page.
const facts = [
  {
    title: 'Who it’s for',
    body: 'Artists, character designers, brands, agencies and creators producing an original designer toy, art toy or mascot figure.',
  },
  {
    title: 'Production routes',
    body: `Molded vinyl from ${figurePricing.vinylMinimum} pieces; hand-painted resin from 1 to 100, and larger resin runs quoted case by case.`,
  },
  {
    title: 'Vinyl mold fee',
    body: `About $${figurePricing.vinylMold} per character design, plus figure production. Each design needs its own mold.`,
  },
  {
    title: 'Prototype first',
    body: `A painted resin prototype you approve before the run: about $${figurePricing.prototype} for a simple ${figurePricing.prototypeHeight} figure in up to ${figurePricing.prototypeColors} colors, with ${figurePricing.revisions} design revisions.`,
  },
  {
    title: 'Timing',
    body: `Prototype typically ${figurePricing.prototypeWeeks}. The production schedule is in your written quote.`,
  },
  {
    title: 'Starting point',
    body: 'A sketch, an AI image, finished artwork, a 3D file or a figure you already have.',
  },
  {
    title: 'Not offered',
    body: 'Articulated action figures with movable joints. Every figure is one fixed pose.',
  },
  {
    title: 'Where',
    body: 'Sculpted, prototyped and hand-painted in our New York studios in Queens and Brooklyn. We ship to all 50 states.',
  },
]

// How a production run moves, in the order the studio quotes it.
const steps = [
  {
    title: 'Send the character.',
    body: 'A sketch, AI render, turnaround art or 3D file, plus the height, colors, how many you want and your date.',
  },
  {
    title: 'Approve the 3D sculpt.',
    body: `We model the figure for production: thick enough to print and mold, one fixed pose, with ${figurePricing.revisions} design revisions.`,
  },
  {
    title: 'Approve the painted prototype.',
    body: 'You hold the figure before anything is made in quantity. It’s the reference every piece in the run is matched to.',
  },
  {
    title: 'We quote and make the run.',
    body: `Resin figures are printed and hand-painted in our studio. Vinyl adds a mold for each design (about $${figurePricing.vinylMold}) and a ${figurePricing.vinylMinimum}-piece minimum.`,
  },
  {
    title: 'Pickup or delivery.',
    body: 'Pick up by appointment in New York or have the run shipped anywhere in the U.S.',
  },
]

const fit = {
  good: [
    'An artist’s limited-edition art toy or designer vinyl release',
    'A brand mascot or character made as a figure for fans, staff or clients',
    'Agency client gifts and activations, like the 500 hand-painted resin figures we made for an ad agency',
    'A creator’s merch figure, prototyped before you commit to a run',
    'A series of characters made as a matching set',
  ],
  other: [
    'Articulated action figures with movable joints: we don’t make them',
    'Plush toys and soft goods',
    'Thousands of low-cost retail units, where an overseas vinyl factory costs far less per piece',
    'Children’s toys that need certified safety testing: we don’t claim it, so ask before you order',
  ],
}

const sendList = [
  'Your character: sketch, AI render, artwork or 3D file',
  'Front, side and back views if you have them',
  'Height and number of paint colors',
  'How many: prototype only, a resin run, or 300+ in vinyl',
  'Your date and delivery ZIP',
]

const faqs = [
  {
    question: 'How does vinyl toy production work?',
    answer: `We sculpt your character in 3D, make a painted resin prototype you approve, then make a production mold from the approved design and produce the vinyl run. Molded vinyl has a ${figurePricing.vinylMinimum}-piece minimum and a mold fee of about $${figurePricing.vinylMold} per character design, plus the cost of the figures. Your written quote confirms the run, the schedule and the delivery.`,
  },
  {
    question: 'What is the minimum order for custom vinyl toys?',
    answer: `${figurePricing.vinylMinimum} pieces per design. Below that, hand-painted resin is usually the better route: we make resin figures from 1 to 100, and larger resin runs are quoted case by case.`,
  },
  {
    question: 'How much does it cost to produce a designer toy?',
    answer: `A simple ${figurePricing.prototypeHeight} resin prototype is about $${figurePricing.prototype}, including 3D modeling, printing and painting in up to ${figurePricing.prototypeColors} solid colors. A vinyl run adds a mold, about $${figurePricing.vinylMold} per character design, plus the figures, priced by size, colors, finish and quantity. Resin projects have a $${figurePricing.resinMinimumBudget} minimum budget. Bigger figures, more colors and accessories are quoted.`,
  },
  {
    question: 'Should I make my designer toy in vinyl or resin?',
    answer: `Resin suits one-offs and small editions: no mold, crisp detail and a hand-painted finish, from 1 to 100 pieces and larger runs on request. Vinyl suits runs of ${figurePricing.vinylMinimum} or more, where the mold fee is spread over many figures. Many projects make the resin prototype first and decide after.`,
  },
  {
    question: 'Can you make more than 100 resin figures?',
    answer:
      'Yes, larger resin runs are quoted case by case. We hand-painted 500 resin character figures for an ad agency’s client gift. Tell us the quantity and date and we’ll quote resin and vinyl side by side.',
  },
  {
    question: 'How long does designer toy production take?',
    answer: `The resin prototype typically takes ${figurePricing.prototypeWeeks}, with ${figurePricing.revisions} design revisions. The production run is scheduled in your quote, based on the route, quantity, paint and your date. Tell us your in-hand date up front.`,
  },
  {
    question: 'Do I need a 3D model to start?',
    answer:
      'No. A sketch, an AI image, finished artwork or a description is enough; modeling is part of the prototype. If you have a 3D file, send it and we’ll check it for production.',
  },
  {
    question: 'Do you make articulated action figures?',
    answer:
      'No. We make designer toys, art toys and collectible figures in one fixed pose, not action figures with movable joints.',
  },
  {
    question: 'Are your figures certified as children’s toys?',
    answer:
      'We make collectibles and display figures. We don’t claim toy-safety certification such as ASTM F963 or CPSIA, so if your figure is sold as a children’s toy, raise it before you order.',
  },
  {
    question: 'Where is PrintAWorld, and do you ship?',
    answer:
      'Our studios are in Glendale, Queens and Brooklyn, New York, where we sculpt, prototype and hand-paint. Pick up by appointment or we ship anywhere in the U.S.',
  },
]

module.exports = {
  page,
  photos,
  photoUrl,
  hero,
  gallery,
  routes,
  facts,
  steps,
  fit,
  sendList,
  faqs,
  figurePricing,
}
